import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { tap, map, switchMap, catchError, delay, filter } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  selectTokenDetails,
  selectActiveTenant,
} from '../selectors/auth.selector';
import * as AuthActions from '../actions/auth.action';
import * as SystemActions from '../actions/system.action';
import {
  AppState,
  Tenant,
  ProfileRole,
  ProfileFeature,
} from '../states/auth.state';
import { AuthService } from 'src/app/services/auth/auth.service';

@Injectable()
export class AuthEffects {
  constructor(
    private readonly authService: AuthService,
    private readonly actions$: Actions,
    private readonly store: Store<AppState>
  ) {}

  login$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.Login),
        tap(() => this.authService.login())
      );
    },
    { dispatch: false }
  );

  changeActiveTenant$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.SetActiveTenant),
      filter(({ active_tenant }) => !!active_tenant),
      tap(({ active_tenant }) =>
        localStorage.setItem(
          `${environment.localStorageKeyPrefix}_tenant`,
          active_tenant.name
        )
      ),
      concatLatestFrom(() => this.store.select(selectTokenDetails)),
      map(([{ active_tenant }, tokenDetails]) =>
        AuthActions.GetUserRoles({
          uniqueId: tokenDetails?.uniqueId ?? '',
          tenant: active_tenant.name,
        })
      )
    );
  });

  changeActiveRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.SetActiveRole),
      filter(({ active_role }) => !!active_role),
      concatLatestFrom(() => this.store.select(selectActiveTenant)),
      map(([{ active_role }, tenant]) =>
        AuthActions.GetUserFeatures({ role: active_role.name, tenant })
      )
    );
  });

  userTenants$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.GetUserTenants),
      switchMap(({ uniqueId }) =>
        this.authService.tenants(uniqueId).pipe(
          switchMap((tenants: Array<Tenant>) => {
            if (tenants.length) {
              return [
                AuthActions.GetUserTenantsSuccess({ uniqueId, tenants }),
                AuthActions.SetActiveTenant({ active_tenant: tenants[0] }),
              ];
            } else {
              return [SystemActions.RedirectToCourtesyPage()];
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.GetUserTenantsFailure({ error }))
          )
        )
      )
    );
  });

  userTenantsFailed$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AuthActions.GetUserTenantsFailure,
          AuthActions.GetUserRolesFailure
        ),
        tap(({ error }) => {
          if (error.status === HttpStatusCode.NotFound) {
            // eslint-disable-next-line @ngrx/no-dispatch-in-effects
            this.store.dispatch(SystemActions.RedirectToCourtesyPage());
          }
        })
      );
    },
    { dispatch: false }
  );

  userRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.GetUserRoles),
      delay(200),
      switchMap(({ uniqueId, tenant }) =>
        this.authService.roles(uniqueId, tenant).pipe(
          map((roles: Array<ProfileRole>) => {
            if (roles.length) {
              return AuthActions.GetUserRolesSuccess({ roles, tenant });
            } else {
              return SystemActions.RedirectToCourtesyPage();
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.GetUserRolesFailure({ error }))
          )
        )
      )
    );
  });

  userRolesSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.GetUserRolesSuccess),
      map(({ roles, tenant }) =>
        AuthActions.GetUserFeatures({ role: roles[0]?.name ?? '', tenant })
      )
    );
  });

  userFeatures$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.GetUserFeatures),
      switchMap(({ role, tenant }) =>
        this.authService.features(role, tenant).pipe(
          map((features: Array<ProfileFeature>) => {
            if (features.length) {
              return AuthActions.GetUserFeaturesSuccess({ features });
            } else {
              return SystemActions.RedirectToCourtesyPage();
            }
          }),
          catchError((error: HttpErrorResponse) =>
            of(AuthActions.GetUserFeaturesFailure({ error }))
          )
        )
      )
    );
  });

  /*   setActiveAccount$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.SetActiveAccount),
        tap(() => this.authService.checkAndSetActiveAccount())
      );
    },
    { dispatch: false }
  ); */

  logout$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.Logout),
        tap(() => this.authService.logout())
      );
    },
    { dispatch: false }
  );
}
